import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  AuthenticationResult,
  LoginRequest,
  RegisterRequest,
  UserInfo,
  UserProvisioningResult,
  GetFeedItemsResult,
  FeedItemVersionDto,
  GetFeedItemsQuery,
  DashboardViewModel,
  MarkAsReadRequest,
  MarkItemAsReadResult,
  MarkAsStarredRequest,
  MarkItemAsStarredResult,
  UpdateSegmentsRequest,
  UpdateSegmentsResult,
  ProcessRssFeedsResult,
  ProductTypeInfo,
  UpdateUserProfileRequest,
  UpdateUserProfileResult,
  ClassificationStatusResult,
  ConfirmEmailRequest,
  EmailConfirmationResult,
  ResendConfirmationRequest,
  ConfirmEmailChangeRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  PasswordResetResult,
  GetNotificationPreferencesResult,
  UpdateNotificationPreferencesRequest,
  UpdateNotificationPreferencesResult,
} from '@/shared/types/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Create axios instance. withCredentials lets the browser send/receive the
// HttpOnly refresh-token cookie that the API sets on login/refresh.
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// The access token lives in memory only - never in localStorage - so XSS cannot
// exfiltrate it. The refresh token is an HttpOnly cookie unreadable by JS.
let accessToken: string | null = null;
export const tokenStorage = {
  getToken: () => accessToken,
  setToken: (token: string) => {
    accessToken = token;
  },
  clearTokens: () => {
    accessToken = null;
  },
};

// The store registers a reset callback here so the response interceptor can clear auth
// state on a failed refresh without importing the store (which would create an import cycle).
type AuthFailureHandler = () => void;
let onAuthFailure: AuthFailureHandler | null = null;
export const setAuthFailureHandler = (handler: AuthFailureHandler) => {
  onAuthFailure = handler;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh tokens are rotated server-side: each successful refresh revokes the
// presented token. Reusing an already-rotated token is treated as theft and
// revokes the whole token family. To avoid that on concurrent 401s, all callers
// share a single in-flight refresh instead of each issuing their own.
let refreshPromise: Promise<string> | null = null;

export const refreshAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    // No body: the refresh token rides along as the HttpOnly cookie. The server
    // rotates it and sets a fresh cookie, returning only the new access token.
    refreshPromise = axios
      .post(`${API_BASE_URL}/auth/refresh-token`, undefined, { withCredentials: true })
      .then((response) => {
        const { token } = response.data;
        tokenStorage.setToken(token);
        return token as string;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const token = await refreshAccessToken();

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - settle auth state in the store before navigating so a
        // concurrent ProtectedRoute render doesn't race this redirect.
        tokenStorage.clearTokens();
        onAuthFailure?.();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API client class
export class ApiClient {
  // Authentication endpoints
  static async login(credentials: LoginRequest): Promise<AuthenticationResult> {
    const response: AxiosResponse<AuthenticationResult> = await api.post('/auth/login', credentials);
    return response.data;
  }

  static async register(userData: RegisterRequest): Promise<UserProvisioningResult> {
    const response: AxiosResponse<UserProvisioningResult> = await api.post('/auth/register', userData);
    return response.data;
  }

  static async confirmEmail(request: ConfirmEmailRequest): Promise<EmailConfirmationResult> {
    const response: AxiosResponse<EmailConfirmationResult> = await api.post('/auth/confirm-email', request);
    return response.data;
  }

  static async resendConfirmation(request: ResendConfirmationRequest): Promise<void> {
    await api.post('/auth/resend-confirmation', request);
  }

  // SC-296: always resolves for a well-formed request - the API never reveals whether the
  // email belongs to an account.
  static async forgotPassword(request: ForgotPasswordRequest): Promise<void> {
    await api.post('/auth/forgot-password', request);
  }

  // SC-296: does not sign the user in; a 400 (rejected or expired link, weak password) rejects
  // with the PasswordResetResult as the response body.
  static async resetPassword(request: ResetPasswordRequest): Promise<PasswordResetResult> {
    const response: AxiosResponse<PasswordResetResult> = await api.post('/auth/reset-password', request);
    return response.data;
  }

  static async confirmEmailChange(request: ConfirmEmailChangeRequest): Promise<AuthenticationResult> {
    // On success the API sets a fresh refresh-token cookie and returns a new access token
    // (the previous sessions are revoked server-side).
    const response: AxiosResponse<AuthenticationResult> = await api.post('/auth/confirm-email-change', request);
    return response.data;
  }

  static async getCurrentUser(): Promise<UserInfo> {
    const response: AxiosResponse<UserInfo> = await api.get('/auth/me');
    return response.data;
  }

  static async updateUserProfile(userData: UpdateUserProfileRequest): Promise<UpdateUserProfileResult> {
    const response: AxiosResponse<UpdateUserProfileResult> = await api.put('/auth/profile', userData);
    return response.data;
  }

  static async getNotificationPreferences(): Promise<GetNotificationPreferencesResult> {
    const response: AxiosResponse<GetNotificationPreferencesResult> = await api.get('/auth/notification-preferences');
    return response.data;
  }

  static async updateNotificationPreferences(request: UpdateNotificationPreferencesRequest): Promise<UpdateNotificationPreferencesResult> {
    const response: AxiosResponse<UpdateNotificationPreferencesResult> = await api.put('/auth/notification-preferences', request);
    return response.data;
  }

  static async logout(): Promise<void> {
    // The refresh token rides along as the HttpOnly cookie; the server revokes it
    // and clears the cookie. No body needed.
    try {
      await api.post('/auth/logout');
    } finally {
      tokenStorage.clearTokens();
    }
  }

  // Feed items endpoints
  static async getFeedItems(query: GetFeedItemsQuery = {}): Promise<GetFeedItemsResult> {
    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'userId' && (isNaN(Number(value)) || Number(value) <= 0)) {
          return;
        }
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, String(v)));
        } else {
          params.append(key, String(value));
        }
      }
    });

    const response: AxiosResponse<GetFeedItemsResult> = await api.get(`/feeditems?${params}`);
    return response.data;
  }

  static async markItemAsRead(feedItemId: number, request: MarkAsReadRequest): Promise<MarkItemAsReadResult> {
    const response: AxiosResponse<MarkItemAsReadResult> = await api.post(
      `/feeditems/${feedItemId}/mark-read`,
      request
    );
    return response.data;
  }

  static async markItemAsStarred(feedItemId: number, request: MarkAsStarredRequest): Promise<MarkItemAsStarredResult> {
    const response: AxiosResponse<MarkItemAsStarredResult> = await api.post(
      `/feeditems/${feedItemId}/mark-starred`,
      request
    );
    return response.data;
  }

  // SC-278: version history of an item, newest first.
  static async getFeedItemVersions(feedItemId: number): Promise<FeedItemVersionDto[]> {
    const response: AxiosResponse<FeedItemVersionDto[]> = await api.get(`/feeditems/${feedItemId}/versions`);
    return response.data;
  }

  // SC-278: admin override to mark an item deleted / restore it.
  static async setItemDeleted(feedItemId: number, isDeleted: boolean): Promise<void> {
    await api.put(`/feeditems/${feedItemId}/deleted`, { isDeleted });
  }

  // The server responds 200 with no body (source is always Manual on a manual
  // set), so the result is built client-side from what was sent.
  static async updateSegments(feedItemId: number, request: UpdateSegmentsRequest): Promise<UpdateSegmentsResult> {
    const url = `/feeditems/${feedItemId}/segments`;
    await api.put(url, request);
    return {
      success: true,
      feedItemId,
      segments: request.segments,
      source: 'Manual',
    };
  }

  // SC-240: admin-only replacement of an item's tags. Responds 200 with no body, so the
  // caller keeps whatever it sent.
  static async updateTags(feedItemId: number, tags: string[]): Promise<void> {
    await api.put(`/feeditems/${feedItemId}/tags`, { tags });
  }

  static async processRssFeeds(): Promise<ProcessRssFeedsResult> {
    const response: AxiosResponse<ProcessRssFeedsResult> = await api.post('/feeditems/process');
    return response.data;
  }

  static async getClassificationStatus(ids: number[]): Promise<ClassificationStatusResult> {
    const response: AxiosResponse<ClassificationStatusResult> = await api.post('/feeditems/classification-status', { ids });
    return response.data;
  }

  // SC-294: per-user unread count computed server-side over the whole corpus, so the dashboard
  // card stays correct even when the item marked read is not on the client's loaded page.
  static async getUnreadCount(): Promise<number> {
    const response: AxiosResponse<{ count: number }> = await api.get('/feeditems/unread-count');
    return response.data.count;
  }

  // RSS feeds endpoints
  static async getProductTypes(): Promise<ProductTypeInfo[]> {
    const response: AxiosResponse<ProductTypeInfo[]> = await api.get('/rssfeeds/product-types');
    return response.data;
  }

  // Dashboard endpoints
  /**
   * SC-421: `today` (yyyy-MM-dd) is the caller's local day — the same one the Today card puts in
   * the feed's date filter — so the server counts that day rather than its own UTC date.
   */
  static async getDashboard(today?: string): Promise<DashboardViewModel> {
    const response: AxiosResponse<DashboardViewModel> = await api.get('/dashboard', {
      params: today ? { today } : undefined,
    });
    return response.data;
  }
}

/** Plain-string bodies longer than this are stack dumps or pages, not messages. */
const MAX_STRING_BODY_MESSAGE = 300;

/**
 * A bare string body worth showing as a message: short, not declared as HTML, and not markup.
 * nginx's built-in 502/504 pages are ~170 chars, so length alone does not rule HTML out.
 */
function plainMessage(data: unknown, headers: unknown): string | undefined {
  if (typeof data !== 'string' || data.length === 0 || data.length >= MAX_STRING_BODY_MESSAGE) return undefined;
  const h = headers as ({ get?: (name: string) => unknown } & Record<string, unknown>) | undefined;
  const contentType = h ? (typeof h.get === 'function' ? h.get('content-type') : h['content-type']) : undefined;
  if (typeof contentType === 'string' && /html|xml/i.test(contentType)) return undefined;
  if (/^\s*</.test(data) || /<\/?(html|head|body)\b/i.test(data)) return undefined;
  return data;
}

export function getErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    // Our handlers write { message }, ProblemDetails carries detail/title, and some 500s are a
    // bare string body — read all three before settling for axios' generic "status code 500".
    const data: unknown = error.response?.data;
    const body = data && typeof data === 'object' ? (data as { message?: unknown; detail?: unknown; title?: unknown }) : {};
    const text = (v: unknown) => (typeof v === 'string' && v.length > 0 ? v : undefined);
    const plain = plainMessage(data, error.response?.headers);
    return text(body.message) ?? text(body.detail) ?? text(body.title) ?? plain ?? (error.message || fallback);
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

export function getErrorStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) return error.response?.status;
  return undefined;
}

export default ApiClient;